import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { UserCircle2, Pencil, X, Key } from "lucide-react";

const Field = ({ label, value }) => (
  <div>
    <div className="sw-label">{label}</div>
    <div className="text-[15px] text-[#323232] font-medium">{value || "—"}</div>
  </div>
);

function EditModal({ user, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ phone: user.phone || "", job_title: user.job_title || "", location: user.location || "", personal_email: user.personal_email || "", emergency_contact: user.emergency_contact || "", bio: user.bio || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch("/me/profile", form); onSaved(); }
    catch (err) { alert(err?.response?.data?.detail || t("common.error_generic")); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between mb-4"><h3 className="sw-heading text-[18px]">{t("profile.edit")}</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div><label className="sw-label">{t("common.job_title")}</label><input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="sw-input" data-testid="profile-job-title" /></div>
          <div><label className="sw-label">{t("common.location")}</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">{t("common.phone")}</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="sw-input" data-testid="profile-phone" /></div>
          <div><label className="sw-label">{t("common.personal_email")}</label><input type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} className="sw-input" /></div>
          <div className="md:col-span-2"><label className="sw-label">{t("common.emergency_contact")}</label><input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} className="sw-input" /></div>
          <div className="md:col-span-2"><label className="sw-label">{t("common.bio")}</label><textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="sw-input" /></div>
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button><button data-testid="save-profile-btn" onClick={save} disabled={saving} className="sw-btn-primary">{saving ? t("loans.saving") : t("common.save")}</button></div>
      </div>
    </div>
  );
}

function PasswordModal({ onClose }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (form.new_password !== form.confirm) { alert(t("profile.password_mismatch")); return; }
    setSaving(true);
    try { await api.post("/me/password", { current_password: form.current_password, new_password: form.new_password }); alert(t("profile.password_updated")); onClose(); }
    catch (err) { alert(err?.response?.data?.detail || t("common.error_generic")); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[440px] w-full p-6 shadow-xl">
        <div className="flex justify-between mb-4"><h3 className="sw-heading text-[18px]">{t("profile.change_password")}</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div><label className="sw-label">{t("profile.current_password")}</label><input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="sw-input" data-testid="current-password" /></div>
          <div><label className="sw-label">{t("profile.new_password")}</label><input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="sw-input" data-testid="new-password" /></div>
          <div><label className="sw-label">{t("profile.confirm_password")}</label><input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="sw-input" /></div>
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button><button data-testid="save-password-btn" onClick={save} disabled={saving || !form.current_password || !form.new_password} className="sw-btn-primary">{saving ? t("loans.saving") : t("profile.update_btn")}</button></div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [dept, setDept] = useState(null);
  const [manager, setManager] = useState(null);
  const [editing, setEditing] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (user?.department_id) api.get("/departments").then((r) => setDept(r.data.find((d) => d.id === user.department_id)));
    if (user?.manager_id) api.get(`/users/${user.manager_id}`).then((r) => setManager(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;
  const initials = user.name.split(" ").map((n) => n[0]).slice(0, 2).join("");

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><UserCircle2 size={22} /></div>
          <h1 className="sw-heading text-[28px]">{t("profile.title")}</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="change-password-btn" onClick={() => setChangingPw(true)} className="sw-btn-secondary"><Key size={14} /> {t("profile.change_password")}</button>
          <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="sw-btn-primary"><Pencil size={14} /> {t("profile.edit")}</button>
        </div>
      </div>

      <div className="sw-card flex flex-col lg:flex-row items-start gap-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#475569] to-[#94a3b8] text-white flex items-center justify-center text-[32px] font-semibold shrink-0">{initials}</div>
        <div className="flex-1">
          <div className="font-semibold text-[22px] text-[#323232]">{user.name}</div>
          <div className="text-[14px] text-[#747474] mt-1">{user.job_title || "—"} · {user.email}</div>
          {user.phone && <div className="text-[13px] text-[#747474]">📱 {user.phone}</div>}
          {user.bio && <div className="text-[13px] text-[#323232] mt-2 italic">{user.bio}</div>}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="sw-badge sw-badge-gold capitalize">{user.role}</span>
            <span className="sw-badge sw-badge-neutral">{user.contract_type?.replace("_", " · ")}</span>
            <span className="sw-badge sw-badge-neutral">{user.location}</span>
          </div>
        </div>
      </div>

      <div className="sw-card grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Field label={t("common.department")} value={dept?.name} />
        <Field label={t("profile.cost_code")} value={user.cost_code} />
        <Field label={t("common.manager")} value={manager?.name} />
        <Field label={t("common.hire_date")} value={user.hire_date?.split("T")[0]} />
        <Field label={t("profile.pto_balance")} value={`${user.pto_balance} ${t("profile.days_unit")}`} />
        <Field label={t("profile.vacation_balance")} value={`${user.vacation_balance} ${t("profile.days_unit")}`} />
        {user.personal_email && <Field label={t("common.personal_email")} value={user.personal_email} />}
        {user.emergency_contact && <Field label={t("common.emergency_contact")} value={user.emergency_contact} />}
      </div>

      {user.payment_components && user.payment_components.length > 0 && (
        <div className="sw-card">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">{t("profile.payment_config")}</div>
          <div className="flex flex-col gap-2">
            {user.payment_components.map((c, i) => (
              <div key={i} className="flex items-center justify-between bg-[#f6f6f6] rounded-[10px] px-4 py-3">
                <div>
                  <div className="font-medium text-[14px] text-[#323232]">{c.label || c.format}</div>
                  <div className="text-[12px] text-[#747474] capitalize">{c.format}</div>
                </div>
                <div className="text-[18px] font-semibold text-[#475569]">
                  ${c.amount}<span className="text-[12px] text-[#747474] font-normal">/{c.format === "hourly" ? "h" : c.format === "daily" ? "d" : c.format === "monthly" ? "mo" : "period"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && <EditModal user={user} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); if (refresh) await refresh(); }} />}
      {changingPw && <PasswordModal onClose={() => setChangingPw(false)} />}
    </div>
  );
}
